import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  LoadingOverlay,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconEdit,
  IconInfoCircle,
  IconPlus,
  IconRefresh,
  IconTrash,
} from '@tabler/icons-react';
import type { Expense, PaymentRequirement } from '@pta/types';
import {
  deleteExpense,
  fetchGroupExpenses,
} from '../api/expenses';
import {
  fetchGroupCurrencyConfig,
  fetchGroupRequirements,
} from '../api/payment-requirements';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useMoneyFormatter } from '../util/money';
import { ExpenseFormModal } from '../components/ExpenseFormModal';

/**
 * Kiadások lapja — csoport-szintű lista. Új kiadásnál a `purpose` mindig
 * kötelező; `requirementId` opcionális (cél-kötés).
 *
 * Pénzformázás: a csoport `GroupLocale`-jából (useMoneyFormatter).
 */
export function ExpensesPage() {
  const { activeGroupId, myGroups, activeRole } = useAuth();
  const { money } = useMoneyFormatter();
  const activeGroup = useMemo(
    () => myGroups.find((g) => g.id === activeGroupId) ?? null,
    [myGroups, activeGroupId],
  );

  const canEdit =
    activeRole === 'pta_cashier' ||
    activeRole === 'institution_admin' ||
    activeRole === 'country_admin' ||
    activeRole === 'systemadmin' ||
    activeRole === 'applicationadmin';

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [requirements, setRequirements] = useState<PaymentRequirement[]>([]);
  const [primaryCurrency, setPrimaryCurrency] = useState('HUF');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure();
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);

  const requirementById = useMemo(() => {
    const m = new Map<number, PaymentRequirement>();
    for (const r of requirements) m.set(r.id, r);
    return m;
  }, [requirements]);

  const load = async () => {
    if (activeGroupId === null) return;
    setLoading(true);
    setError(null);
    try {
      const [exp, reqs] = await Promise.all([
        fetchGroupExpenses(activeGroupId),
        fetchGroupRequirements(activeGroupId),
      ]);
      setExpenses(exp);
      setRequirements(reqs);
      try {
        const cfg = await fetchGroupCurrencyConfig(activeGroupId);
        setPrimaryCurrency(cfg.primaryCurrency);
      } catch {
        setPrimaryCurrency('HUF');
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [activeGroupId]);

  if (activeGroupId === null) {
    return (
      <Stack gap="md">
        <Title order={2}>Kiadások</Title>
        <Alert color="yellow" icon={<IconInfoCircle size={18} />}>
          Nincs aktív csoport kiválasztva.
        </Alert>
      </Stack>
    );
  }

  const handleNew = () => {
    setEditingExpense(null);
    openModal();
  };

  const handleEdit = (e: Expense) => {
    setEditingExpense(e);
    openModal();
  };

  const handleDelete = (e: Expense) => {
    modals.openConfirmModal({
      title: 'Kiadás törlése',
      children: (
        <Stack gap="xs">
          <Text>Biztosan törlöd ezt a kiadást? Ez a művelet auditálva van.</Text>
          <Text fw={500}>
            {e.purpose} — {money(e.amount)}
          </Text>
        </Stack>
      ),
      labels: { confirm: 'Törlés', cancel: 'Mégse' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteExpense(e.id);
          notifications.show({ color: 'green', message: 'Törölve.' });
          await load();
        } catch (err) {
          notifications.show({
            color: 'red',
            message: err instanceof ApiError ? err.message : String(err),
          });
        }
      },
    });
  };

  return (
    <Stack gap="md" pos="relative">
      <LoadingOverlay visible={loading} />

      <Group justify="space-between">
        <Title order={2}>{activeGroup?.name ?? `#${activeGroupId}`} — Kiadások</Title>
        <Group>
          <Tooltip label="Frissítés">
            <ActionIcon onClick={() => void load()} variant="light">
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          {canEdit && (
            <Button leftSection={<IconPlus size={16} />} onClick={handleNew}>
              Új kiadás
            </Button>
          )}
        </Group>
      </Group>

      {error && <Alert color="red">{error}</Alert>}

      <Card withBorder p={0}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Dátum</Table.Th>
              <Table.Th>Cél</Table.Th>
              <Table.Th>Cél-kötés</Table.Th>
              <Table.Th ta="right">Összeg</Table.Th>
              <Table.Th>Mód</Table.Th>
              <Table.Th>Akinek</Table.Th>
              <Table.Th>Számla</Table.Th>
              {canEdit && <Table.Th style={{ width: 90 }}>Műveletek</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {expenses.length === 0 && !loading && (
              <Table.Tr>
                <Table.Td colSpan={canEdit ? 8 : 7} align="center">
                  Nincs rögzített kiadás.
                </Table.Td>
              </Table.Tr>
            )}
            {expenses.map((e) => {
              const r = e.requirementId !== null ? requirementById.get(e.requirementId) : null;
              return (
                <Table.Tr key={e.id}>
                  <Table.Td>{e.spentAt}</Table.Td>
                  <Table.Td>{e.purpose}</Table.Td>
                  <Table.Td>
                    {r ? (
                      <Text size="sm">#{r.id} — {r.title}</Text>
                    ) : (
                      <Text size="xs" c="dimmed">általános</Text>
                    )}
                  </Table.Td>
                  <Table.Td ta="right">
                    <strong>{money(e.amount)}</strong>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" size="sm">{e.paymentMethod}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{e.payeeName ?? '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{e.invoiceNumber ?? '—'}</Text>
                  </Table.Td>
                  {canEdit && (
                    <Table.Td>
                      <Group gap={4}>
                        <ActionIcon variant="subtle" onClick={() => handleEdit(e)}>
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => handleDelete(e)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Card>

      <ExpenseFormModal
        opened={modalOpened}
        onClose={() => {
          closeModal();
          setEditingExpense(null);
        }}
        groupId={activeGroupId}
        currency={primaryCurrency}
        requirements={requirements}
        expense={editingExpense}
        onSaved={async () => {
          closeModal();
          setEditingExpense(null);
          await load();
        }}
      />
    </Stack>
  );
}
